/**
 * Admin Verification Queue
 * Allows admins to review and approve/reject pending verifications
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { verifications } from '../../lib/api';
import { ClipboardList, CheckCircle2, XCircle, Loader2, Building2, User, Info } from 'lucide-react';
import { toast } from 'sonner';

export const AdminVerificationQueue = () => {
  const [verificationList, setVerificationList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionType, setActionType] = useState('approve'); // 'approve' | 'reject'
  const [rejectionReason, setRejectionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchVerifications();
  }, [activeTab]);

  const fetchVerifications = async () => {
    try {
      setLoading(true);
      const type = activeTab === 'all' ? null : activeTab;
      const response = await verifications.getPending(type);
      setVerificationList(response.data.verifications || []);
    } catch (error) {
      console.error('Error fetching verifications:', error);
      toast.error('Failed to load pending verifications');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async () => {
    if (!selectedVerification) return;

    if (actionType === 'reject' && !rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    try {
      setSubmitting(true);
      
      const response = await verifications.approveHotel(selectedVerification.verification_id, {
        approved: actionType === 'approve',
        verified_by: 'admin',
        rejection_reason: actionType === 'reject' ? rejectionReason : null
      });

      if (response.data.success) {
        toast.success(
          actionType === 'approve' 
            ? '✅ Verification approved successfully!' 
            : 'Verification rejected'
        );
        setDialogOpen(false);
        setRejectionReason('');
        await fetchVerifications();
      } else {
        toast.error(response.data.message || 'Action failed');
      }
    } catch (error) {
      console.error('Action error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process verification');
    } finally {
      setSubmitting(false);
    }
  };

  const openActionDialog = (verification, type) => {
    setSelectedVerification(verification);
    setActionType(type);
    setDialogOpen(true);
  };

  const getDocumentIcon = (docType) => {
    if (docType === 'guest_id') return <User className="w-4 h-4" />;
    if (docType === 'hotel_business') return <Building2 className="w-4 h-4" />;
    return <ClipboardList className="w-4 h-4" />;
  };

  const renderVerificationRow = (verification) => {
    const isGuest = verification.document_type === 'guest_id';
    const isHotel = verification.document_type === 'hotel_business';

    return (
      <TableRow key={verification.verification_id} data-testid={`verification-row-${verification.verification_id}`}>
        <TableCell>
          <div className="flex items-center gap-2">
            {getDocumentIcon(verification.document_type)}
            <span className="mono text-xs">{verification.verification_id}</span>
          </div>
        </TableCell>
        <TableCell>
          <Badge variant="outline" className="text-xs">
            {isGuest ? 'Guest ID' : isHotel ? 'Hotel Business' : verification.document_type}
          </Badge>
        </TableCell>
        <TableCell className="text-sm">
          {verification.user_email || verification.owner_email || 'N/A'}
        </TableCell>
        <TableCell className="text-sm">
          {verification.hotel_id || 'N/A'}
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {new Date(verification.uploaded_at).toLocaleString()}
        </TableCell>
        <TableCell>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="default"
              onClick={() => openActionDialog(verification, 'approve')}
              disabled={submitting}
              data-testid={`approve-btn-${verification.verification_id}`}
            >
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Approve
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => openActionDialog(verification, 'reject')}
              disabled={submitting}
              data-testid={`reject-btn-${verification.verification_id}`}
            >
              <XCircle className="w-3 h-3 mr-1" />
              Reject
            </Button>
          </div>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <>
      <Card data-testid="admin-verification-queue" className="border-border">
        <CardHeader>
          <div className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5" />
            <CardTitle>Verification Queue</CardTitle>
          </div>
          <CardDescription>
            Review and approve pending guest ID and hotel business verifications
          </CardDescription>
        </CardHeader>

        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full">
              <TabsTrigger value="all" className="flex-1" data-testid="tab-all">
                All ({verificationList.length})
              </TabsTrigger>
              <TabsTrigger value="guest_id" className="flex-1" data-testid="tab-guests">
                Guest IDs
              </TabsTrigger>
              <TabsTrigger value="hotel_business" className="flex-1" data-testid="tab-hotels">
                Hotel Business
              </TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab} className="mt-4">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : verificationList.length === 0 ? (
                <Alert>
                  <Info className="w-4 h-4" />
                  <AlertDescription>
                    No pending verifications at the moment.
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>ID</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Hotel ID</TableHead>
                        <TableHead>Uploaded</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {verificationList.map(renderVerificationRow)}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Action Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="verification-action-dialog">
          <DialogHeader>
            <DialogTitle>
              {actionType === 'approve' ? 'Approve Verification' : 'Reject Verification'}
            </DialogTitle>
            <DialogDescription>
              {selectedVerification && (
                <span className="mono text-xs">{selectedVerification.verification_id}</span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {actionType === 'approve' ? (
              <Alert>
                <CheckCircle2 className="w-4 h-4" />
                <AlertDescription>
                  This will mark the verification as approved and update the user/hotel status.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="rejection-reason">Reason for Rejection *</Label>
                <Textarea
                  id="rejection-reason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Provide a clear reason for rejection..."
                  rows={4}
                  data-testid="rejection-reason-input"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              variant={actionType === 'approve' ? 'default' : 'destructive'}
              onClick={handleAction}
              disabled={submitting}
              data-testid="confirm-action-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  {actionType === 'approve' ? (
                    <><CheckCircle2 className="w-4 h-4 mr-2" />Approve</>
                  ) : (
                    <><XCircle className="w-4 h-4 mr-2" />Reject</>
                  )}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};